import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { common, createLowlight } from 'lowlight';
import 'highlight.js/styles/github-dark.css';
import useCollab from '../../../hooks/useCollab';
import useViewerRole from '../../../hooks/useViewerRole';
import useDisclosure from '../../../hooks/useDisclosure';
import { useAuth } from '../../../context/AuthContext';
import { colorForId } from '../../../lib/color';
import { parseMarkdown, serializeDoc } from '../../../lib/tiptapMarkdown';
import CodeBlockEnhanced from '../../../extensions/CodeBlockEnhanced';
import { HiddenBlock, HiddenPlaceholder } from '../../../extensions/HiddenBlock';
import SlashCommand from '../../../extensions/SlashCommand';
import ScreenplayDecorations from '../../../extensions/ScreenplayDecorations';
import CharMark from '../../../extensions/CharMark';
import CharAutocomplete from '../../../extensions/CharAutocomplete';
import LinkModal from '../LinkModal/LinkModal';
import CharacterPickerModal from '../CharacterPickerModal/CharacterPickerModal';
import DialogBlockModal from '../DialogBlockModal/DialogBlockModal';
import TransitionPickerModal from '../TransitionPickerModal/TransitionPickerModal';
import SceneHeadingModal from '../SceneHeadingModal/SceneHeadingModal';
import SlashMenu from '../SlashMenu/SlashMenu';
import CharAutocompleteMenu from '../CharAutocompleteMenu/CharAutocompleteMenu';
import Icon from '../../ui/Icon/Icon';
import styles from './ChapterEditor.module.css';

const FONTS = {
  serif: 'var(--font-serif)',
  sans: 'var(--font-sans)',
  mono: 'var(--font-mono)',
};

// runSlashItem — invoked when user picks a menu entry. `props` carries the
// suggestion lifecycle's editor + range. `openLink` is injected by the host
// so the Link item can open the brutalist link modal.
function runSlashItem(item, props, ctx) {
  if (!item || !item.command) return;
  item.command({ editor: props.editor, range: props.range, ...ctx });
}

const lowlight = createLowlight(common);

function buildScreenplaySlashItems(query, { includeScene = false } = {}) {
  const q = (query || '').toLowerCase().trim();
  const all = [];
  if (includeScene) {
    all.push({
      key: 'scn', section: 'screenplay', label: 'Scene heading', hint: 'INT./EXT.',
      command: ({ editor, range, openScene }) => {
        editor.chain().focus().deleteRange(range).run();
        openScene?.();
      },
    });
  }
  all.push(
    { key: 'chr', section: 'screenplay', label: 'Character', hint: 'modal',
      command: ({ editor, range, openCharacter }) => {
        editor.chain().focus().deleteRange(range).run();
        openCharacter?.();
      } },
    { key: 'dlg', section: 'screenplay', label: 'Dialog block', hint: 'modal',
      command: ({ editor, range, openDialog }) => {
        editor.chain().focus().deleteRange(range).run();
        openDialog?.();
      } },
    { key: 'trn', section: 'screenplay', label: 'Transition', hint: 'modal',
      command: ({ editor, range, openTransition }) => {
        editor.chain().focus().deleteRange(range).run();
        openTransition?.();
      } },
  );
  if (!q) return all;
  return all.filter((it) =>
    it.label.toLowerCase().includes(q) || it.key.includes(q),
  );
}

// Slash-menu command catalogue. Each item: { key, label, hint, section,
// command({ editor, range }) }. `range` is the `/query` span supplied by the
// suggestion plugin — every command deletes it first, then performs the
// action so the trigger text never lingers in the doc.
function buildSlashItems(canHidden, query) {
  const q = (query || '').toLowerCase().trim();
  const all = [
    { key: 'h1', section: 'basics', label: 'Heading 1', hint: '#',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run() },
    { key: 'h2', section: 'basics', label: 'Heading 2', hint: '##',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run() },
    { key: 'h3', section: 'basics', label: 'Heading 3', hint: '###',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run() },
    { key: 'ul', section: 'basics', label: 'Bullet list', hint: '- ',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBulletList().run() },
    { key: 'ol', section: 'basics', label: 'Numbered list', hint: '1.',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleOrderedList().run() },
    { key: 'quote', section: 'basics', label: 'Quote', hint: '>',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBlockquote().run() },
    { key: 'hr', section: 'basics', label: 'Divider', hint: '---',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setHorizontalRule().run() },

    { key: 'bold', section: 'inline', label: 'Bold', hint: '**',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBold().run() },
    { key: 'italic', section: 'inline', label: 'Italic', hint: '_',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleItalic().run() },
    { key: 'underline', section: 'inline', label: 'Underline', hint: 'Ctrl+U',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleUnderline().run() },
    { key: 'code', section: 'inline', label: 'Inline code', hint: '`',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleCode().run() },
    { key: 'strike', section: 'inline', label: 'Strikethrough', hint: '~~',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleStrike().run() },

    { key: 'codeblock', section: 'blocks', label: 'Code block', hint: '```',
      command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setCodeBlock().run() },
    { key: 'link', section: 'links', label: 'Link / chapter ref', hint: '↗',
      command: ({ editor, range, openLink }) => {
        editor.chain().focus().deleteRange(range).run();
        openLink?.();
      } },
  ];
  if (canHidden) {
    all.push({
      key: 'hidden', section: 'blocks', label: 'Hidden block', hint: 'owner/admin',
      command: ({ editor, range }) => {
        const id = crypto.randomUUID();
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'hiddenBlock',
            attrs: { id, visible: [] },
            content: [{ type: 'paragraph' }],
          })
          .run();
      },
    });
  }
  if (!q) return all;
  return all.filter((it) =>
    it.label.toLowerCase().includes(q) || it.key.includes(q),
  );
}

// Collaborative rich editor. Markdown stays the wire format on the WebSocket
// (zero backend change). Tiptap owns the in-memory ProseMirror doc; we
// serialize on every local change and parse incoming snapshots back to a doc.
// No more Write/Preview toggle — the editor renders inline (Notion-style).
export default function ChapterEditor({ chapter, workspaceId, codexId, chapters = [], codexType = 'general', codexSubtype = 'scaletta', knownCharacters = [], onLocalContentChange, sceneJumpRef }) {
  const navigate = useNavigate();
  const isScreenplay = codexType === 'screenplay';
  const isPlainScreenplay = isScreenplay && codexSubtype === 'plain';
  const [font, setFont] = useState(isScreenplay ? 'mono' : 'serif');
  const linkModal = useDisclosure();
  const characterModal = useDisclosure();
  const dialogModal = useDisclosure();
  const transitionModal = useDisclosure();
  const sceneHeadingModal = useDisclosure();
  const lastLocalEdit = useRef(0);
  const sendTimer = useRef(null);
  const sendRef = useRef(null);
  const applyingRemote = useRef(false);
  const slashApi = useRef(null);
  const charMenuApi = useRef(null);
  const allKnownCharactersRef = useRef([]);
  const canHiddenRef = useRef(false);
  const openLinkRef = useRef(null);
  const openCharacterRef = useRef(null);
  const openDialogRef = useRef(null);
  const openTransitionRef = useRef(null);
  const openSceneRef = useRef(null);
  const editorBoxRef = useRef(null);
  const onLocalContentChangeRef = useRef(onLocalContentChange);
  onLocalContentChangeRef.current = onLocalContentChange;
  const [scrollEdge, setScrollEdge] = useState('top');
  // 'idle' = nothing pending. 'unsaved' = user typed, debounce not elapsed.
  // 'saving' = sent over WS, waiting on server flush to S3 / DB. 'saved' =
  // server confirmed persistence (fades to idle after a short delay). Drives
  // the “Saving… / Saved” toolbar chip — anchored to durable state, not the
  // in-memory ack (which only proves the hub accepted the snapshot).
  const [saveStatus, setSaveStatus] = useState('idle');
  const savedTimer = useRef(null);
  // Server flushes every ~10s. We give it 25s of slack before assuming the
  // “saved” event got lost (transient WS hiccup, server without the new
  // event, etc.) and silently flipping back to idle — better than lying with a
  // perpetual “Saving…”. Reset on each new edit / chapter change.
  const savingFallbackTimer = useRef(null);

  const { user } = useAuth();
  const { role } = useViewerRole(workspaceId);
  const canHidden = role === 'owner' || role === 'admin';
  canHiddenRef.current = canHidden;
  const isScreenplayRef = useRef(isScreenplay);
  isScreenplayRef.current = isScreenplay;
  const isPlainScreenplayRef = useRef(isPlainScreenplay);
  isPlainScreenplayRef.current = isPlainScreenplay;
  const myName = user?.name || user?.email || 'Guest';
  const myColor = colorForId(user?.id || '');
  const myAvatar = user?.avatarUrl || '';

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ codeBlock: false }),
        CodeBlockEnhanced.configure({ lowlight, defaultLanguage: null }),
        HiddenBlock,
        HiddenPlaceholder,
        Underline,
        Link.configure({ openOnClick: false, autolink: true }),
        ...(isScreenplay
          ? [
              ScreenplayDecorations,
              CharMark,
              CharAutocomplete.configure({
                minLength: 2,
                getItems: ({ query }) => {
                  if (!query || query.length < 2) return [];
                  if (query.startsWith('/')) return [];
                  const q = query.toUpperCase();
                  return (allKnownCharactersRef.current || [])
                    .filter((n) => n.startsWith(q))
                    .slice(0, 8)
                    .map((n) => ({ name: n, label: n }));
                },
                // Case preservation: first occurrence (typed via CHR modal) is
                // always UPPERCASE; subsequent occurrences via autocomplete
                // mirror the writer's typed case — if they typed any lowercase
                // letter we title-case the canonical name (JOÃO → João), else
                // we keep it uppercase. The autocomplete only ever suggests
                // already-known characters, so no `init` attr here.
                onAccept: ({ editor: ed, item, query, range }) => {
                  const canonical = item.name || '';
                  const allUpper = query === query.toUpperCase();
                  const text = allUpper
                    ? canonical
                    : canonical.charAt(0).toUpperCase() +
                      canonical.slice(1).toLowerCase();
                  ed.chain()
                    .focus()
                    .deleteRange(range)
                    .insertContent([
                      { type: 'text', text, marks: [{ type: 'char' }] },
                      { type: 'text', text: ' ' },
                    ])
                    .unsetMark('char')
                    .run();
                },
                onStart: (state) => charMenuApi.current?.setProps(state),
                onUpdate: (state) => charMenuApi.current?.setProps(state),
                onExit: () => charMenuApi.current?.close(),
                onKeyDown: (event) => charMenuApi.current?.onKeyDown(event) || false,
              }),
            ]
          : []),
        Placeholder.configure({
          placeholder: isScreenplay
            ? 'Type "/" for screenplay blocks (ACT, CHR, DLG, PAR, TRN).'
            : 'Start writing… type "/" for commands. Markdown shortcuts work.',
        }),
        SlashCommand.configure({
          suggestion: {
            items: ({ query }) =>
              isScreenplayRef.current
                ? buildScreenplaySlashItems(query, { includeScene: isPlainScreenplayRef.current })
                : buildSlashItems(canHiddenRef.current, query),
            render: () => ({
              onStart: (props) => slashApi.current?.setProps({ ...props, command: (item) => runSlashItem(item, props, {
                openLink: openLinkRef.current,
                openCharacter: openCharacterRef.current,
                openDialog: openDialogRef.current,
                openTransition: openTransitionRef.current,
                openScene: openSceneRef.current,
              }) }),
              onUpdate: (props) => slashApi.current?.setProps({ ...props, command: (item) => runSlashItem(item, props, {
                openLink: openLinkRef.current,
                openCharacter: openCharacterRef.current,
                openDialog: openDialogRef.current,
                openTransition: openTransitionRef.current,
                openScene: openSceneRef.current,
              }) }),
              onKeyDown: (props) => slashApi.current?.onKeyDown(props) || false,
              onExit: () => slashApi.current?.close(),
            }),
          },
        }),
      ],
      content: '',
      onUpdate: ({ editor: ed }) => {
        if (applyingRemote.current) return;
        lastLocalEdit.current = Date.now();
        const markdown = serializeDoc(ed.state.doc);
        onLocalContentChangeRef.current?.(chapter?.id, markdown);
        setSaveStatus('unsaved');
        clearTimeout(savedTimer.current);
        clearTimeout(sendTimer.current);
        clearTimeout(savingFallbackTimer.current);
        sendTimer.current = setTimeout(() => {
          const sent = sendRef.current?.(markdown);
          if (sent !== false) {
            setSaveStatus('saving');
            clearTimeout(savingFallbackTimer.current);
            savingFallbackTimer.current = setTimeout(() => {
              setSaveStatus((s) => (s === 'saving' ? 'idle' : s));
            }, 25000);
          }
        }, 250);
      },
    },
    [chapter?.id, isScreenplay],
  );

  const applyRemote = (markdown) => {
    if (!editor || editor.isDestroyed) return;
    try {
      const doc = parseMarkdown(editor.schema, markdown || '');
      applyingRemote.current = true;
      editor.commands.setContent(doc.toJSON(), false);
    } catch (err) {
      console.error('[ChapterEditor] failed to parse remote markdown', err);
    } finally {
      applyingRemote.current = false;
    }
  };

  const onRemote = (remote, type) => {
    if (type === 'init') {
      // Fresh connection / reconnection: server is the source of truth now,
      // so any lingering “saving…” from before the reconnect is meaningless.
      clearTimeout(savingFallbackTimer.current);
      setSaveStatus((s) => (s === 'saving' || s === 'unsaved' ? 'idle' : s));
      const recentEdit = Date.now() - lastLocalEdit.current < 500;
      if (remote && remote.length && !recentEdit) {
        applyRemote(remote);
      } else if (editor && !editor.isEmpty && (!remote || !remote.length)) {
        // Push the current local doc up if the server is empty (new chapter).
        sendRef.current?.(serializeDoc(editor.state.doc));
      }
      return;
    }
    if (Date.now() - lastLocalEdit.current > 400) {
      applyRemote(remote);
    }
  };

  // Ack callback: server accepted the snapshot into the in-memory hub. NOT a
  // durability signal — keep showing "Saving…" until the flush event lands.
  const onAck = () => {};

  // Saved callback: hub's flush successfully wrote the snapshot to S3 / DB.
  // Flip to “Saved” for a moment, then back to idle so the chip doesn’t loiter.
  const onSaved = () => {
    clearTimeout(savingFallbackTimer.current);
    setSaveStatus('saved');
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus('idle'), 1500);
  };

  const { presence, live, loaded, cursors, sendEdit } = useCollab(chapter?.id, onRemote, onAck, onSaved);
  sendRef.current = sendEdit;

  // Flush any pending debounced edit when the chapter changes or the component
  // unmounts so the server gets the latest snapshot.
  useEffect(() => {
    setSaveStatus('idle');
    clearTimeout(savedTimer.current);
    clearTimeout(savingFallbackTimer.current);
    return () => {
      clearTimeout(sendTimer.current);
      clearTimeout(savedTimer.current);
      clearTimeout(savingFallbackTimer.current);
      if (editor && !editor.isDestroyed) {
        const md = serializeDoc(editor.state.doc);
        if (md) sendRef.current?.(md);
      }
    };
  }, [chapter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track scroll position inside the ProseMirror surface so we can show a
  // floating jump button: at top → jump-to-end, at bottom → jump-to-start.
  useEffect(() => {
    const box = editorBoxRef.current;
    if (!box) return undefined;
    const pm = box.querySelector('.ProseMirror');
    if (!pm) return undefined;
    const compute = () => {
      const max = pm.scrollHeight - pm.clientHeight;
      if (max <= 8) {
        setScrollEdge('none');
        return;
      }
      if (pm.scrollTop <= 4) setScrollEdge('top');
      else if (pm.scrollTop >= max - 4) setScrollEdge('bottom');
      else setScrollEdge('middle');
    };
    compute();
    pm.addEventListener('scroll', compute, { passive: true });
    const ro = new ResizeObserver(compute);
    ro.observe(pm);
    return () => {
      pm.removeEventListener('scroll', compute);
      ro.disconnect();
    };
  }, [editor, chapter?.id]);

  const jumpScroll = () => {
    const box = editorBoxRef.current;
    if (!box) return;
    const pm = box.querySelector('.ProseMirror');
    if (!pm) return;
    if (scrollEdge === 'top' || scrollEdge === 'middle') {
      pm.scrollTo({ top: pm.scrollHeight, behavior: 'smooth' });
    } else if (scrollEdge === 'bottom') {
      pm.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll to first character cue matching the dispatched name.
  useEffect(() => {
    if (!isScreenplay) return undefined;
    const onJump = (e) => {
      const name = (e.detail?.name || '').toUpperCase();
      if (!name) return;
      const el = document.querySelector(`[data-character="${CSS.escape(name)}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.addEventListener('screenplay:jump-character', onJump);
    return () => window.removeEventListener('screenplay:jump-character', onJump);
  }, [isScreenplay]);

  // Imperative scene jump for the SceneIndex sidebar (plain screenplays).
  // CodexPage owns the ref; we publish a function that scrolls the Nth
  // `.screenplay-scene` paragraph into view. Recomputed on chapter switch so
  // a stale closure can't target an unmounted ProseMirror surface.
  useEffect(() => {
    if (!sceneJumpRef) return undefined;
    sceneJumpRef.current = (index) => {
      const box = editorBoxRef.current;
      if (!box) return;
      const pm = box.querySelector('.ProseMirror');
      if (!pm) return;
      const scenes = pm.querySelectorAll('.screenplay-scene');
      const target = scenes[index];
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return () => {
      if (sceneJumpRef.current) sceneJumpRef.current = null;
    };
  }, [sceneJumpRef, chapter?.id]);

  const peers = [
    { id: 'me', name: `${myName} (you)`, color: myColor, avatar: myAvatar },
    ...Object.entries(cursors).map(([id, c]) => ({
      id,
      name: c.name || 'Guest',
      color: c.color,
      avatar: c.avatar,
    })),
  ];

  // Live-merge server-provided knownCharacters with whatever's currently
  // typed in this editor session, so the picker's autocomplete sees names the
  // writer just added before the chapter has flushed back to the server.
  const allKnownCharacters = useMemo(() => {
    const set = new Set((knownCharacters || []).map((n) => String(n).toUpperCase()));
    if (editor && !editor.isDestroyed) {
      try {
        editor.state.doc.descendants((node) => {
          if (node.isText) {
            const hasCharMark = (node.marks || []).some(
              (m) => m.type?.name === 'char',
            );
            if (hasCharMark) {
              const n = (node.text || '').trim().toUpperCase();
              if (n) set.add(n);
              return;
            }
            // Backwards-compat: any chapter saved before the mark migration
            // still has literal `<char>NAME</char>` text on the wire. The
            // parser now turns those into marked text on load, but be lenient
            // in case some old doc lingers in memory mid-session.
            const t = node.text || '';
            const re = /<char>([^<]+)<\/char>/gi;
            let m;
            while ((m = re.exec(t)) !== null) {
              const n = m[1].trim().toUpperCase();
              if (n) set.add(n);
            }
            return;
          }
          if (node.type.name === 'paragraph') {
            const t = (node.textContent || '').trim();
            const m = /^@@CHR\s+(.+)$/i.exec(t);
            if (m) {
              const n = m[1].trim().toUpperCase();
              if (n) set.add(n);
            }
          }
        });
      } catch (_err) {
        // doc walk is best-effort; never block the picker.
      }
    }
    return Array.from(set).sort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, knownCharacters, characterModal.isOpen, dialogModal.isOpen]);

  // Keep the ref in sync so CharAutocomplete's getItems callback (captured at
  // editor construction) always reads the latest known-character list.
  allKnownCharactersRef.current = allKnownCharacters;

  if (!chapter) return null;

  const cmd = (fn) => () => {
    if (!editor) return;
    fn(editor.chain().focus()).run();
  };

  const insertLink = () => {
    if (!editor) return;
    linkModal.open();
  };
  openLinkRef.current = insertLink;

  const submitLink = (href) => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    linkModal.close();
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    linkModal.close();
  };

  const currentHref = editor?.getAttributes('link')?.href || '';

  // Click handler on the editor surface. Any click on an `<a>` element
  // navigates: internal (`/workspaces/...`) via SPA router, external in a new
  // tab. Caret placement still works because tiptap's own click handler runs
  // first on non-link targets.
  const onProseClick = (e) => {
    const a = e.target.closest?.('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    if (href.startsWith('/workspaces/')) {
      navigate(href);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const insertHidden = () => {
    if (!editor) return;
    const id = crypto.randomUUID();
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'hiddenBlock',
        attrs: { id, visible: [] },
        content: [{ type: 'paragraph' }],
      })
      .run();
  };

  const openCharacter = () => characterModal.open();
  const openDialog = () => dialogModal.open();
  const openTransition = () => transitionModal.open();
  const openSceneHeading = () => sceneHeadingModal.open();
  openCharacterRef.current = openCharacter;
  openDialogRef.current = openDialog;
  openTransitionRef.current = openTransition;
  openSceneRef.current = openSceneHeading;

  const submitCharacter = (name) => {
    if (editor) {
      // Insert the bare name as a text node with the `char` mark applied, then
      // unset the mark on the active selection so the next keystroke escapes
      // the wrapper. No literal `<` `>` chars ever enter the doc, so the
      // caret can't get trapped inside an invisible close tag.
      const upper = String(name || '').trim().toUpperCase();
      if (!upper) {
        characterModal.close();
        return;
      }
      // Stamp `init=chapter.id` only when this is the character's first
      // appearance anywhere in the codex (allKnownCharacters spans every
      // scene). The decoupage pipeline reads this attr to anchor a character
      // to the scene that introduced them.
      const isNew = !allKnownCharacters.includes(upper);
      const charMark =
        isNew && chapter?.id
          ? { type: 'char', attrs: { init: chapter.id } }
          : { type: 'char' };
      editor
        .chain()
        .focus()
        .insertContent([
          { type: 'text', text: upper, marks: [charMark] },
          { type: 'text', text: ' ' },
        ])
        .unsetMark('char')
        .run();
    }
    characterModal.close();
  };

  const submitDialog = ({ character, paren, body }) => {
    if (!editor) {
      dialogModal.close();
      return;
    }
    const upper = String(character || '').trim().toUpperCase();
    // Same init-stamping rule as submitCharacter: only the codex-wide first
    // appearance carries the scene id.
    const isNew = upper && !allKnownCharacters.includes(upper);
    const charMark =
      isNew && chapter?.id
        ? { type: 'char', attrs: { init: chapter.id } }
        : { type: 'char' };
    const paragraphs = [];
    paragraphs.push({
      type: 'paragraph',
      content: upper
        ? [{ type: 'text', text: upper, marks: [charMark] }]
        : [],
    });
    if (paren) {
      paragraphs.push({
        type: 'paragraph',
        content: [{ type: 'text', text: `@@PAR (${paren})` }],
      });
    }
    paragraphs.push({
      type: 'paragraph',
      content: [{ type: 'text', text: `@@DLG ${body || ''}` }],
    });
    paragraphs.push({ type: 'paragraph' });
    const { $from } = editor.state.selection;
    const chain = editor.chain().focus();
    if ($from.parent.type.name === 'paragraph' && $from.parent.content.size === 0) {
      chain.insertContentAt({ from: $from.before(), to: $from.after() }, paragraphs).run();
    } else {
      chain.insertContent(paragraphs).run();
    }
    const tail = editor.state.doc.content.size - 1;
    editor.commands.setTextSelection(tail);
    dialogModal.close();
  };

  // Transitions are inserted at the caret as a single `@@TRN ...` paragraph
  // followed by an empty paragraph, so the writer can keep typing below it.
  const submitTransition = (value) => {
    if (!editor) {
      transitionModal.close();
      return;
    }
    const trnText = `@@TRN ${value}`;
    editor
      .chain()
      .focus()
      .insertContent([
        { type: 'paragraph', content: [{ type: 'text', text: trnText }] },
        { type: 'paragraph' },
      ])
      .run();
    transitionModal.close();
  };

  // Scene heading (`@@SCN ...`) — fluid screenplay (plain) format. Insert
  // the heading paragraph at the caret with a trailing empty paragraph so the
  // writer drops straight into action text after the slug line.
  const submitSceneHeading = (value) => {
    if (!editor) {
      sceneHeadingModal.close();
      return;
    }
    const scnText = `@@SCN ${value}`;
    editor
      .chain()
      .focus()
      .insertContent([
        { type: 'paragraph', content: [{ type: 'text', text: scnText }] },
        { type: 'paragraph' },
      ])
      .run();
    sceneHeadingModal.close();
  };

  const isActive = (name, attrs) =>
    editor?.isActive(name, attrs) ? `${styles.tool} ${styles.toolActive}` : styles.tool;

  return (
    <div className={`${styles.editor} ${isScreenplay ? styles.screenplay : ''}`}>
      <div className={styles.toolbar}>
        <div className={styles.tools}>
          {isScreenplay ? (
            <>
              {isPlainScreenplay && (
                <button type="button" className={styles.tool} title="Scene heading" onClick={openSceneHeading}>SCENE</button>
              )}
              <button type="button" className={styles.tool} title="Character" onClick={openCharacter}>CHARACTER</button>
              <button type="button" className={styles.tool} title="Dialog block" onClick={openDialog}>DIALOG</button>
              <button type="button" className={styles.tool} title="Transition" onClick={openTransition}>TRANSITION</button>
            </>
          ) : (
            <>
              <button type="button" className={isActive('heading', { level: 1 })} title="Heading 1" onClick={cmd((c) => c.toggleHeading({ level: 1 }))}>H1</button>
              <button type="button" className={isActive('heading', { level: 2 })} title="Heading 2" onClick={cmd((c) => c.toggleHeading({ level: 2 }))}>H2</button>
              <button type="button" className={isActive('heading', { level: 3 })} title="Heading 3" onClick={cmd((c) => c.toggleHeading({ level: 3 }))}>H3</button>
              <span className={styles.sep} />
              <button type="button" className={`${isActive('bold')} ${styles.bold}`} title="Bold (Ctrl/⌘+B)" onClick={cmd((c) => c.toggleBold())}>B</button>
              <button type="button" className={`${isActive('italic')} ${styles.italic}`} title="Italic (Ctrl/⌘+I)" onClick={cmd((c) => c.toggleItalic())}>I</button>
              <button type="button" className={`${isActive('underline')} ${styles.under}`} title="Underline (Ctrl/⌘+U)" onClick={cmd((c) => c.toggleUnderline())}>U</button>
              <button type="button" className={isActive('strike')} title="Strikethrough" onClick={cmd((c) => c.toggleStrike())}>S</button>
              <button type="button" className={isActive('code')} title="Inline code" onClick={cmd((c) => c.toggleCode())}>{'</>'}</button>
              <span className={styles.sep} />
              <button type="button" className={isActive('bulletList')} title="Bullet list" onClick={cmd((c) => c.toggleBulletList())}>•</button>
              <button type="button" className={isActive('orderedList')} title="Numbered list" onClick={cmd((c) => c.toggleOrderedList())}>1.</button>
              <button type="button" className={isActive('blockquote')} title="Quote" onClick={cmd((c) => c.toggleBlockquote())}>"</button>
              <span className={styles.sep} />
              <button type="button" className={isActive('codeBlock')} title="Code block" onClick={cmd((c) => c.toggleCodeBlock())}>CODE</button>
              <button type="button" className={isActive('link')} title="Link" onClick={insertLink}>LINK</button>
              {canHidden && (
                <button type="button" className={isActive('hiddenBlock')} title="Hidden block (owner/admin only)" onClick={insertHidden}>HID</button>
              )}
            </>
          )}
        </div>

        <div className={styles.right}>
          <span className={styles.saveStatus} title="Save status">
            {saveStatus === 'unsaved' && 'Unsaved changes'}
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && (
              <>
                <span className={styles.savedDot} />
                Saved
              </>
            )}
          </span>
          <span className={styles.status} title="Collaboration status">
            <span className={live ? styles.dotLive : styles.dotOff} />
            {live ? `Live · ${presence}` : 'Offline'}
          </span>
          <select
            className={styles.fontSelect}
            value={font}
            onChange={(e) => setFont(e.target.value)}
            title="Writing font"
          >
            <option value="serif">Serif</option>
            <option value="sans">Sans</option>
            <option value="mono">Mono</option>
          </select>
        </div>
      </div>

      <h2 className={styles.title}>{chapter.title}</h2>

      <div className={styles.area}>
        {!isScreenplay && (
          <aside className={styles.presence}>
            {peers.map((p) => (
              <div key={p.id} className={styles.presenceItem} title={p.name}>
                {p.avatar ? (
                  <img src={p.avatar} alt={p.name} className={styles.presenceAvatar} />
                ) : (
                  <span className={styles.presenceAvatar} style={{ background: p.color }}>
                    {(p.name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <span className={styles.presenceName}>{p.name}</span>
              </div>
            ))}
          </aside>
        )}
        <div className={styles.editorBox} ref={editorBoxRef}>
          <EditorContent
            editor={editor}
            className={styles.prose}
            style={{ fontFamily: FONTS[font] }}
            onClick={onProseClick}
          />
          {!loaded && (
            <div className={styles.loadingOverlay} role="status" aria-live="polite">
              <span className={styles.loadingDot} />
              Loading content…
            </div>
          )}
          {scrollEdge !== 'none' && scrollEdge !== 'bottom' && (
            <button
              type="button"
              className={styles.scrollJump}
              onClick={jumpScroll}
              title="Jump to bottom"
              aria-label="Jump to bottom"
            >
              <Icon name="ChevronsDown" size={16} />
            </button>
          )}
        </div>
      </div>

      <LinkModal
        isOpen={linkModal.isOpen}
        onClose={linkModal.close}
        onSubmit={submitLink}
        onRemove={currentHref ? removeLink : undefined}
        initialHref={currentHref}
        workspaceId={workspaceId}
        codexId={codexId}
        chapters={chapters}
        currentChapterId={chapter?.id}
      />

      {isScreenplay && (
        <>
          <CharacterPickerModal
            isOpen={characterModal.isOpen}
            onClose={characterModal.close}
            onSubmit={submitCharacter}
            known={allKnownCharacters}
          />
          <DialogBlockModal
            isOpen={dialogModal.isOpen}
            onClose={dialogModal.close}
            onSubmit={submitDialog}
            known={allKnownCharacters}
          />
          <TransitionPickerModal
            isOpen={transitionModal.isOpen}
            onClose={transitionModal.close}
            onSubmit={submitTransition}
          />
          {isPlainScreenplay && (
            <SceneHeadingModal
              isOpen={sceneHeadingModal.isOpen}
              onClose={sceneHeadingModal.close}
              onSubmit={submitSceneHeading}
            />
          )}
        </>
      )}

      <SlashMenu apiRef={slashApi} />
      {isScreenplay && <CharAutocompleteMenu apiRef={charMenuApi} />}
    </div>
  );
}
